import { cloneElement, isValidElement, ReactNode, UIEvent, useCallback, useEffect, useRef, useState } from 'react'

import { StickyScrollbar } from './elements/StickyScrollbar'
import * as Styled from './styles'
import { checkHorizontalOverflow } from './utils/checkHorizontalOverflow'
import { syncStickyHeaderScroll } from './utils/syncStickyHeaderScroll'
import { syncTableColumnWidths } from './utils/syncTableColumnWidths'

interface TableProps {
  allExpandableIds: string[]
  children: ReactNode
  header?: ReactNode
}

const STICKY_HEADER_THRESHOLD = 91

export const ReportTable = ({ children, header }: TableProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const stickyHeaderRef = useRef<HTMLDivElement>(null)
  const scrollbarRef = useRef<HTMLDivElement>(null)
  const syncTimeoutRef = useRef<number | null>(null)

  const [showShadow, setShowShadow] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isHeaderSticky, setIsHeaderSticky] = useState(false)
  const [isScrollbarSticky, setIsScrollbarSticky] = useState(false)
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false)
  const [tableScrollLeft, setTableScrollLeft] = useState(0)
  const [tableScrollWidth, setTableScrollWidth] = useState(0)

  const handleContainerScroll = useCallback(() => {
    if (!containerRef.current) {
      return
    }

    const rect = containerRef.current.getBoundingClientRect()
    const isSticky = rect.top < STICKY_HEADER_THRESHOLD

    setIsHeaderSticky(isSticky)

    if (scrollbarRef.current) {
      const scrollbarRect = scrollbarRef.current.getBoundingClientRect()

      const isScrollbarStickyNow = scrollbarRect.bottom >= window.innerHeight && rect.bottom > window.innerHeight

      setIsScrollbarSticky(isScrollbarStickyNow)
    }
  }, [])

  useEffect(() => {
    const container = document.querySelector('.simplebar-content-wrapper')
    if (container) {
      container.addEventListener('scroll', handleContainerScroll)
      handleContainerScroll()

      return () => container.removeEventListener('scroll', handleContainerScroll)
    }
  }, [handleContainerScroll])

  useEffect(() => {
    const syncWidths = () => {
      if (!containerRef.current) {
        return
      }

      if (isHeaderSticky) {
        syncTableColumnWidths(containerRef.current)
      }

      const { hasOverflow, scrollWidth } = checkHorizontalOverflow(containerRef.current)

      setHasHorizontalOverflow(hasOverflow)
      setTableScrollWidth(scrollWidth)
    }

    const resizeObserver = new ResizeObserver((entries) => {
      const hasOriginalTableChange = entries.some((entry) => entry.target.closest('[data-sticky-header]') === null)
      // Only sync if the change is from the original table, not the sticky header
      if (!hasOriginalTableChange) {
        return
      }

      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
      }
      syncTimeoutRef.current = window.setTimeout(() => {
        syncWidths()
      }, 50)
    })

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    setTimeout(() => {
      syncWidths()
      const scrollLeft = containerRef.current?.scrollLeft || 0
      syncStickyHeaderScroll(scrollLeft, stickyHeaderRef.current)
    }, 0)

    return () => {
      resizeObserver.disconnect()

      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
      }
    }
  }, [isHeaderSticky])

  const handleScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    /* showing shadow on first column when it is overflowing data */
    const target = event.currentTarget
    const scrollLeft = target.scrollLeft
    setIsScrolled(scrollLeft > 0)
    setTableScrollLeft(scrollLeft)

    const { scrollWidth, clientWidth } = checkHorizontalOverflow(target)
    const maxScrollLeft = scrollWidth - clientWidth
    const shouldShowShadow = scrollLeft < maxScrollLeft
    setShowShadow(shouldShowShadow)

    syncStickyHeaderScroll(scrollLeft, stickyHeaderRef.current)
  }, [])

  const handleScrollbarScroll = useCallback((scrollLeft: number) => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = scrollLeft
    }

    syncStickyHeaderScroll(scrollLeft, stickyHeaderRef.current)
  }, [])

  /* In default view, determine whether to show shadow on the right side
  to indicate scrollable view */
  useEffect(() => {
    if (containerRef.current) {
      const { hasOverflow, scrollWidth } = checkHorizontalOverflow(containerRef.current)
      setShowShadow(hasOverflow)
      setHasHorizontalOverflow(hasOverflow)
      setTableScrollWidth(scrollWidth)
    }
  }, [])

  return (
    <>
      {/* The only way to make header sticky with overflow-x */}
      {header && isValidElement(header) && (
        <Styled.StickyHeaderDuplicate
          isVisible={isHeaderSticky}
          isScrolled={isScrolled}
          data-sticky-header
          ref={stickyHeaderRef}
        >
          {cloneElement(header, {
            ...header.props,
            key: 'sticky-header-duplicate',
          })}
        </Styled.StickyHeaderDuplicate>
      )}

      <Styled.TableWrapper>
        <Styled.TableContainer ref={containerRef} onScroll={handleScroll} isScrolled={isScrolled}>
          <Styled.TableBody>
            {
              <>
                {header}
                {children}
              </>
            }
          </Styled.TableBody>
        </Styled.TableContainer>
        <Styled.RightShadow showShadow={showShadow} />

        <StickyScrollbar
          ref={scrollbarRef}
          hasHorizontalOverflow={hasHorizontalOverflow}
          onScroll={handleScrollbarScroll}
          tableScrollLeft={tableScrollLeft}
          tableScrollWidth={tableScrollWidth}
          isScrollbarSticky={isScrollbarSticky}
        />
      </Styled.TableWrapper>
    </>
  )
}
